import {Component, OnDestroy, OnInit} from '@angular/core';
import {PlainButtonComponent} from '../buttons/plain-button.component';
import {ThemeService} from '../../services/theme-service';
import {ActivatedRoute, Router} from '@angular/router';
import {OrderService} from '../../services/order.service';
import {AppStateService} from '../../services/app-state.service';
import {ModalService} from '../../services/modal.service';
import {ServingUtil} from '../../utils/serving-util';
import {Subscription} from 'rxjs';
import { VenueConfig } from '../../models/FSConfig';
import { DeliveryType } from '../../models/Identity';
import { TranslateService } from '../../services/translate.service';
import { CommonModule } from '@angular/common';
import { ChangeDtDialogComponent } from '../dialogs/change-dt-dialog.component';
import { LoadingSpinnerComponent } from "../ui/loading-spinner-component";
import {BorderIconButtonComponent} from '../buttons/border-icon-button.component';
import {AppState} from '../../models/AppState';
import {ConfigService} from '../../services/config.service';

@Component({
  selector: 'app-start-page',
  standalone: true,
  imports: [
    PlainButtonComponent,
    CommonModule,
    LoadingSpinnerComponent,
    BorderIconButtonComponent,
  ],
  templateUrl: './start-page.component.html',
})
export class StartPageComponent implements OnInit, OnDestroy {
  imageUrl?: string;
  logoUrl?: string;
  private sub?: Subscription;
  //byt meny
  list?: any[];
  isLoading = false;
  closeable = true;
  private subDialog?: Subscription;
  private subSmartTable?: Subscription;
  private subChangeDelivery?: Subscription;
  config?: VenueConfig;
  venueName?: string;
  isNewVisit = false;
  callout: string = "";
  fromBook = false;
  bookingEnabled = false;

  constructor(private themeService: ThemeService, private router: Router, private route: ActivatedRoute, private modalService: ModalService,
              private appStateService: AppStateService, private translate: TranslateService, private order: OrderService,
              private cfg: ConfigService) {}

  ngOnInit(): void {
    this.fromBook = this.route.snapshot.queryParamMap.get('from') === 'book';
    this.sub = this.appStateService.observeAppState().subscribe(appState => {
      this.venueName = appState?.cfg?.name;
      this.config = appState?.cfg;
      this.bookingEnabled = appState?.cfg?.tablebooking?.enabled ?? false;
      this.buildList(appState);

      this.isNewVisit = appState.order.user_cfg.dt == null;
      this.callout = this.fromBook ? this.getString('Våra menyer')
        : this.isNewVisit ? this.getString('Hur vill du beställa?') : this.getString('... eller byt leveranssätt');

      const branding = appState?.cfg.branding;
      if (branding?.background_file) {
        this.imageUrl = ServingUtil.getDynamicImage(branding.background_file, 0);
      }
      if (branding?.logo_file) {
        this.logoUrl = ServingUtil.getDynamicImage(branding.logo_file, 300, false, true);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.subDialog?.unsubscribe();
    this.subSmartTable?.unsubscribe();
    this.subChangeDelivery?.unsubscribe();
  }

  navMenu() {
    this.router.navigate([this.appStateService.venueId, 'menu', 'food']);
  }

  // Tables are booked on beeker, the venue's booking page
  navBook() {
    window.location.href = `${this.cfg.booking_url}/${this.appStateService.venueId}`;
  }

  private buildList(appState: AppState): void {
    const cfg: VenueConfig = appState?.cfg;
    const dt = appState?.order?.user_cfg?.dt;
    this.list = [];
    if (cfg.dt?.inhouse && dt !== DeliveryType.Table) {
      if (cfg.dt.inhouse.selectable == null || cfg.dt.inhouse.selectable === true) {
        this.list.push({ title: cfg.dt.inhouse.title, desc: cfg.dt.inhouse.desc ?? '', key: DeliveryType.Table });
      }
    }
    if (cfg.dt?.takeaway && dt !== DeliveryType.Takeaway) {
      this.list.push({ title: cfg.dt.takeaway.title, desc: cfg.dt.takeaway.desc ?? '', key: DeliveryType.Takeaway });
    }
    if (cfg.dt?.roomservice && dt !== DeliveryType.Roomservice) {
      this.list.push({ title: cfg.dt.roomservice.title, desc: cfg.dt.roomservice.desc ?? '', key: DeliveryType.Roomservice });
    }
    if (cfg.dt?.pickup && dt !== DeliveryType.Pickup) {
      if (cfg.dt.pickup.selectable == null || cfg.dt.pickup.selectable === true){
        this.list.push({ title: cfg.dt?.pickup?.title, desc: cfg.dt?.pickup?.desc ?? '', key: DeliveryType.Pickup });
      }
    }
    if (cfg.dt?.foodcourt && dt !== DeliveryType.Foodcourt) {
      this.list.push({ title: cfg.dt.foodcourt.title, desc: cfg.dt.foodcourt.desc ?? '', key: DeliveryType.Foodcourt });
    }
  }

  openLink(event: MouseEvent, key: string): void {
    event.preventDefault();
    this.isLoading = true;

    const order = this.order.getOrderOrNull();
    const hasItems = (order?.items?.length ?? 0) > 0;
    const currentDeliveryType = order?.user_cfg?.dt;
    const isSmartTableActive = order?.user_cfg?.smart_table != null;

    if ((hasItems && currentDeliveryType !== key) || isSmartTableActive) {
      const warnings = [];
      if (hasItems && currentDeliveryType !== key) {
        warnings.push('Om du byter leveranssätt så kommer din beställning att tömmas.');
      }
      if (isSmartTableActive) {
        warnings.push('Du har tidigare scannat en QR-kod som är kopplad till ett kundkonto, det kommer att kopplas bort.');
      }

      const dialogRef = this.modalService.show(ChangeDtDialogComponent, {
        data: {
          title: warnings.length > 1 ? 'Lämna SmartTable och byta leveranssätt?' : 'Byta leveranssätt?',
          message: warnings.join('<br><br>'),
          confirmText: 'Ja',
          abortText: 'Nej',
        }
      });

      this.subDialog = dialogRef.subscribe({
        next: (confirmed) => {
          if (confirmed) {
            this.handleSmartTableAndDeliveryChange(isSmartTableActive, key);
          } else {
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('Error handling dialog result:', err);
          this.isLoading = false;
        }
      });
    } else {
      if (order?.user_cfg.dt === key) {
        this.navMenu();
        return;
      }
      this.changeDeliveryType(key);
    }
  }

  menuOfTheWeek(): boolean {
    return this.config?.show_overview ?? false;
  }

  navMenuOfTheWeek() {
    this.router.navigate([this.appStateService.venueId, 'week']);
  }

  private handleSmartTableAndDeliveryChange(isSmartTableActive: boolean, key: string): void {
    if (isSmartTableActive) {
      this.subSmartTable = this.order.leaveSmartTable().subscribe({
        next: () => {
          console.log('SmartTable lämnat.');
          this.changeDeliveryType(key);
        },
        error: (err) => {
          console.error('Fel vid lämning av SmartTable:', err);
        },
      });
    } else {
      this.changeDeliveryType(key);
    }
  }

  private changeDeliveryType(key: string): void {
    this.subChangeDelivery = this.order.changeDeliverType(key).subscribe({
      next: () => {
        console.log('Leveranstyp ändrad till:', key);
        this.navMenu();
      },
      error: (err) => {
        console.error('Fel vid ändring av leveranstyp:', err);
      },
    });
  }

  getVenueName(): string {
    return this.venueName ?? '';
  }

  getString(s: string) {
    return this.translate.single(s);
  }
}
